import React from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import AdSenseWrapper from './AdSenseWrapper'

interface AdSenseManagerProps {
    page: 'home' | 'poemDetail' | 'admin' | 'login'
    position: 'header' | 'sidebar-left' | 'sidebar-right' | 'content-top' | 'content-middle' | 'content-bottom' | 'footer'
    show?: boolean
}

const AdSenseManager: React.FC<AdSenseManagerProps> = ({
    page,
    position,
    show = true
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    // Don't show ads on admin or login pages
    if (page === 'admin' || page === 'login') {
        return null
    }

    // Don't show sidebar ads on mobile
    if (isMobile && (position === 'sidebar-left' || position === 'sidebar-right')) {
        return null
    }

    if (!show) {
        return null
    }

    const getAdConfig = () => {
        switch (position) {
            case 'header':
                return {
                    type: 'placeholder' as const,
                    variant: 'banner' as const,
                    position: 'top' as const,
                    responsive: true,
                    sticky: false
                }

            case 'sidebar-left':
            case 'sidebar-right':
                return {
                    type: 'placeholder' as const,
                    variant: 'skyscraper' as const,
                    position: 'side' as const,
                    responsive: false,
                    sticky: true
                }

            case 'content-top':
                return {
                    type: 'placeholder' as const,
                    variant: 'rectangle' as const,
                    position: 'content' as const,
                    responsive: true,
                    sticky: false
                }

            case 'content-middle':
                return {
                    type: 'placeholder' as const,
                    variant: 'square' as const,
                    position: 'content' as const,
                    responsive: true,
                    sticky: false
                }

            case 'content-bottom':
                return {
                    type: 'placeholder' as const,
                    variant: 'rectangle' as const,
                    position: 'content' as const,
                    responsive: true,
                    sticky: false
                }

            case 'footer':
                return {
                    type: 'placeholder' as const,
                    variant: 'banner' as const,
                    position: 'bottom' as const,
                    responsive: true,
                    sticky: false
                }

            default:
                return {
                    type: 'placeholder' as const,
                    variant: 'banner' as const,
                    position: 'content' as const,
                    responsive: true,
                    sticky: false
                }
        }
    }

    const config = getAdConfig()

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            ...(position === 'footer' ? { mt: 2, mb: 1 } : {}),
            ...(position.includes('sidebar') && {
                position: 'sticky',
                top: 100,
                zIndex: 1
            })
        }}>
            <AdSenseWrapper {...config} />
        </Box>
    )
}

export default AdSenseManager
